/* GERAT / Ella 6.6.2. Informational only: never writes cart or payment data. */
(() => {
  'use strict';
  const integer = value => {
    if (typeof value === 'string' && !/^\d+$/.test(value)) return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const result = Number(value);
    return Number.isSafeInteger(result) && result >= 0 ? result : null;
  };
  const validConfig = c => Boolean(c && c.enabled && c.country === 'MX' && c.currency === 'MXN'
    && ['shippingMin', 'threeMin', 'sixMin'].every(k => integer(c[k]) > 0)
    && c.sixMin >= c.threeMin && c.text);
  const monthly = (cents, months) => {
    if (integer(cents) === null || ![3, 6].includes(months)) return null;
    return Number((BigInt(cents) + BigInt(Math.floor(months / 2))) / BigInt(months));
  };
  const productTotal = (price, quantity) => {
    if (integer(price) === null || integer(quantity) === null || quantity < 1) return null;
    const total = BigInt(price) * BigInt(quantity);
    return total <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(total) : null;
  };
  const money = (cents, compact = false) => new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', minimumFractionDigits: compact && cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(cents / 100);
  const fill = (value, vars) => String(value || '').replace(/\{([a-z_]+)\}/g, (all, key) => vars[key] ?? all);
  function benefits(c, amount, context) {
    if (!validConfig(c) || integer(amount) === null || amount <= 0 || !c[context]) return null;
    const months = amount >= c.sixMin ? 6 : amount >= c.threeMin ? 3 : 0;
    const vars = {three_min: money(c.threeMin, true), six_min: money(c.sixMin, true), shipping_min: money(c.shippingMin, true)};
    const text = (key, extra) => fill(c.text[key], {...vars, ...extra});
    let finance = '', shipping = '', more = '';
    const combined = c.finance && c.shipping && c.shippingMin === c.threeMin && !months && context === 'cart';
    if (c.finance) {
      if (context === 'product') finance = months
        ? text('productPayment', {months, amount: money(monthly(amount, months))}) : text('productHint');
      else finance = months ? text(months === 6 ? 'cartSix' : 'cartThree')
        : text(combined ? 'cartCombined' : 'cartMissing', {amount: money(c.threeMin - amount)});
      if (context === 'cart' && months === 3) more = text('cartMore', {amount: money(c.sixMin - amount)});
    }
    if (c.shipping && !combined) shipping = amount >= c.shippingMin ? text('shippingEligible')
      : context === 'product' ? text('shippingHint') : text('shippingMissing', {amount: money(c.shippingMin - amount)});
    if (!finance && !shipping) return null;
    const contentVars = {amount: money(amount)};
    return {
      amount, context, months, shippingEligible: amount >= c.shippingMin,
      options: [3, 6].map(term => ({
        months: term, eligible: term <= months,
        installment: term <= months ? monthly(amount, term) : null,
        minimum: term === 3 ? c.threeMin : c.sixMin,
        missing: Math.max(0, (term === 3 ? c.threeMin : c.sixMin) - amount)
      })),
      shippingInfo: shipping || (c.shipping ? text('shippingMissing', {amount: money(c.shippingMin - amount)}) : ''),
      finance, shipping, more,
      three: c.finance && months === 6 ? text('productPayment', {months: 3, amount: money(monthly(amount, 3))}) : '',
      mercadoInstructions: c.finance ? text('mercadoInstructions', contentVars) : '',
      cardInstructions: c.finance ? text('cardInstructions', contentVars) : '',
      conditions: c.finance ? text('conditions', contentVars) : '', shippingConditions: c.shipping ? text('shippingConditions', contentVars) : '',
      summary: text('details'), credit: Boolean(c.finance), delivery: Boolean(c.shipping)
    };
  }
  const core = {integer, validConfig, monthly, productTotal, benefits, money};
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined' || !window.customElements || customElements.get('gerat-benefits')) return;

  let configSource, configValue;
  function config() {
    const source = document.getElementById('gerat-benefits-config')?.textContent;
    if (source !== configSource) {
      configSource = source;
      try { configValue = JSON.parse(source); } catch (_) { configValue = null; }
    }
    return configValue;
  }
  const instances = new Set();
  function cartInstances() { return [...instances].filter(el => el.dataset.context === 'cart' && el.isConnected); }

  // One informational dialog for product and cart; it never selects a payment method.
  let dialog, activeOwner, activeOpener, scrollState, selectedPayment = 'mercado';
  const renderedCopy = new WeakMap();
  // Only paragraphs, numbered steps and **emphasis**; editable HTML is always text.
  function renderCopy(node, value) {
    const source = String(value || '');
    if (renderedCopy.get(node) === source) return;
    const fragment = document.createDocumentFragment();
    const inline = (parent, text) => {
      let at = 0;
      for (const match of text.matchAll(/\*\*(.+?)\*\*/g)) {
        parent.append(document.createTextNode(text.slice(at, match.index)));
        const strong = document.createElement('strong');
        strong.textContent = match[1];
        parent.append(strong);
        at = match.index + match[0].length;
      }
      parent.append(document.createTextNode(text.slice(at)));
    };
    let paragraph, list;
    for (const line of source.replace(/\r\n?/g, '\n').split('\n')) {
      const text = line.trim();
      if (!text) { paragraph = list = null; continue; }
      const step = text.match(/^(\d{1,4})\.\s+(.+)$/);
      if (step) {
        paragraph = null;
        if (!list) { list = document.createElement('ol'); list.start = Number(step[1]); fragment.append(list); }
        const item = document.createElement('li');
        item.value = Number(step[1]);
        inline(item, step[2]);
        list.append(item);
      } else {
        list = null;
        if (!paragraph) { paragraph = document.createElement('p'); fragment.append(paragraph); }
        else paragraph.append(document.createTextNode(' '));
        inline(paragraph, text);
      }
    }
    node.replaceChildren(fragment);
    renderedCopy.set(node, source);
  }
  function selectPaymentTab(name, focus = false) {
    const tabs = [...dialog.querySelectorAll('[data-gerat-payment-tab]')];
    const target = tabs.find(tab => !tab.hidden && tab.dataset.geratPaymentTab === name) || tabs.find(tab => !tab.hidden);
    selectedPayment = target?.dataset.geratPaymentTab || 'mercado';
    tabs.forEach(tab => {
      const selected = tab === target;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      dialog.querySelector(`[data-gerat-payment-panel="${tab.dataset.geratPaymentTab}"]`).hidden = !selected;
    });
    if (focus) target?.focus();
  }
  function modalStatus(message) {
    if (!dialog?.open) return;
    dialog.querySelector('[data-gerat-modal-content]').hidden = true;
    const status = dialog.querySelector('[data-gerat-modal-status]');
    status.textContent = message;
    status.hidden = false;
  }
  function paintModal(owner) {
    if (activeOwner !== owner || !dialog?.open) return;
    const model = owner.model;
    if (!model) return modalStatus('No podemos confirmar un importe elegible en este momento. Revisa la selección o el carrito y vuelve a consultar.');
    const set = (selector, value) => { dialog.querySelector(selector).textContent = value; };
    const isProduct = model.context === 'product';
    set('[data-gerat-modal-context]', isProduct ? 'Importe estimado del producto' : 'Importe estimado del pedido');
    set('[data-gerat-modal-amount]', money(model.amount) + ' MXN');
    set('[data-gerat-modal-units]', `${owner.units || 1} ${(owner.units || 1) === 1 ? 'unidad' : 'unidades'} · Cálculo provisional`);
    set('[data-gerat-modal-cash]', money(model.amount));
    model.options.forEach(option => {
      const row = dialog.querySelector(`[data-gerat-modal-plan="${option.months}"]`);
      row.hidden = !model.credit;
      row.toggleAttribute('data-recommended', option.eligible && option.months === model.months);
      row.toggleAttribute('data-unavailable', !option.eligible);
      row.querySelector('[data-gerat-modal-installment]').textContent = option.eligible ? money(option.installment) + ' al mes' : 'No disponible';
      row.querySelector('[data-gerat-modal-plan-minimum]').textContent = option.eligible
        ? 'Total estimado: ' + money(model.amount)
        : `Desde ${money(option.minimum, true)} · `;
      const missing = row.querySelector('[data-gerat-modal-plan-missing]');
      missing.textContent = option.eligible ? '' : `Te faltan ${money(option.missing)}`;
      missing.hidden = option.eligible;
    });
    dialog.querySelector('[data-gerat-modal-shipping]').hidden = !model.delivery;
    set('[data-gerat-modal-shipping-label]', model.shippingInfo);
    const instructions = {mercado: model.mercadoInstructions, card: model.cardInstructions};
    Object.entries(instructions).forEach(([name, text]) => {
      dialog.querySelector(`[data-gerat-payment-tab="${name}"]`).hidden = !String(text || '').trim();
      renderCopy(dialog.querySelector(`[data-gerat-payment-panel="${name}"]`), text);
    });
    dialog.querySelector('[data-gerat-modal-instructions]').hidden = !model.credit || !Object.values(instructions).some(text => String(text || '').trim());
    selectPaymentTab(selectedPayment);
    renderCopy(dialog.querySelector('[data-gerat-modal-shipping-terms]'), model.shippingConditions);
    renderCopy(dialog.querySelector('[data-gerat-modal-conditions]'), model.conditions);
    dialog.querySelector('[data-gerat-modal-finance-terms]').hidden = !model.conditions;
    dialog.querySelector('[data-gerat-modal-delivery-terms]').hidden = !model.shippingConditions;
    dialog.querySelector('[data-gerat-modal-product-note]').hidden = !isProduct;
    dialog.querySelector('[data-gerat-modal-status]').hidden = true;
    dialog.querySelector('[data-gerat-modal-content]').hidden = false;
  }
  function lockPage() {
    const body = document.body;
    const properties = ['overflow', 'position', 'top', 'left', 'right', 'width', 'padding-right'];
    const fixed = getComputedStyle(body).position === 'fixed';
    const gap = window.innerWidth - document.documentElement.clientWidth;
    scrollState = {x: window.scrollX, y: window.scrollY, fixed,
      styles: properties.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)])};
    if (gap > 0) body.style.setProperty('padding-right', `${(parseFloat(getComputedStyle(body).paddingRight) || 0) + gap}px`);
    body.style.setProperty('overflow', 'hidden');
    if (!fixed) {
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-scrollState.y}px`);
      body.style.setProperty('left', '0');
      body.style.setProperty('right', '0');
      body.style.setProperty('width', '100%');
    }
  }
  function finishModal() {
    if (!scrollState && !activeOwner) return;
    const owner = activeOwner, opener = activeOpener, state = scrollState;
    activeOwner = activeOpener = scrollState = null;
    if (state) {
      state.styles.forEach(([name, value, priority]) => value
        ? document.body.style.setProperty(name, value, priority) : document.body.style.removeProperty(name));
      if (!state.fixed) {
        const root = document.documentElement, previous = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(state.x, state.y);
        root.style.scrollBehavior = previous;
      }
    }
    if (opener?.isConnected && getComputedStyle(opener).visibility !== 'hidden' && opener.getClientRects().length) opener.focus({preventScroll: true});
    else {
      const fallback = (owner?.root?.isConnected && owner.root.querySelector('select, input:not([type="hidden"]), button'))
        || document.querySelector('[data-cart-sidebar], #cart-checkout, #cart-sidebar-checkout');
      fallback?.focus({preventScroll: true});
    }
  }
  function closeModal() {
    if (dialog?.open) dialog.close();
    finishModal();
  }
  function ensureModal() {
    if (dialog?.isConnected) return dialog;
    const template = document.getElementById('gerat-benefits-modal-template');
    if (!template || typeof HTMLDialogElement === 'undefined') return null;
    document.body.append(template.content.cloneNode(true));
    dialog = document.getElementById('gerat-benefits-dialog');
    dialog.querySelector('[data-gerat-close]').addEventListener('click', closeModal);
    dialog.querySelectorAll('[data-gerat-payment-tab]').forEach(tab => {
      tab.addEventListener('click', () => selectPaymentTab(tab.dataset.geratPaymentTab));
      tab.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const tabs = [...dialog.querySelectorAll('[data-gerat-payment-tab]')].filter(item => !item.hidden);
        const index = tabs.indexOf(tab);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
        selectPaymentTab(tabs[next].dataset.geratPaymentTab, true);
      });
    });
    dialog.addEventListener('close', finishModal);
    dialog.addEventListener('cancel', event => { event.preventDefault(); event.stopPropagation(); closeModal(); });
    // Keep both Escape events inside the dialog so Ella's drawer stays open.
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
      if (event.key === 'Tab') {
        event.stopPropagation();
        const focusable = [...dialog.querySelectorAll('summary, button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter(el => {
            const closed = el.closest('details:not([open])');
            return el.tabIndex >= 0 && (!closed || el === closed.querySelector('summary'))
              && !el.closest('[hidden]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden';
          });
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (!first || !focusable.includes(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }
    });
    dialog.addEventListener('keyup', event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeModal(); }
    });
    const outside = event => {
      const rect = dialog.querySelector('.gerat-benefits-modal__panel').getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };
    let startedOutside = false;
    dialog.addEventListener('pointerdown', event => { startedOutside = event.target === dialog && outside(event); });
    dialog.addEventListener('click', event => {
      event.stopPropagation();
      if (startedOutside && event.target === dialog && outside(event)) closeModal();
      startedOutside = false;
    });
    return dialog;
  }
  function openModal(owner, opener) {
    if (!owner.model || !ensureModal()) return;
    if (dialog.open) closeModal();
    activeOwner = owner;
    activeOpener = opener;
    selectedPayment = 'mercado';
    dialog.querySelectorAll('.gerat-benefits-modal__disclosure').forEach(section => { section.open = false; });
    lockPage();
    dialog.showModal();
    dialog.querySelector('.gerat-benefits-modal__body').scrollTop = 0;
    dialog.querySelector('#gerat-benefits-title').focus({preventScroll: true});
    if (owner.dataset.context === 'cart') cartRefresh();
    else owner.updateProduct();
  }

  class GeratBenefits extends HTMLElement {
    connectedCallback() {
      const template = document.getElementById('gerat-benefits-template');
      if (!template || !validConfig(config())) return;
      if (!this.querySelector('.gerat-benefits__messages')) this.append(template.content.cloneNode(true));
      this.openButton = this.querySelector('[data-gerat-open]');
      this.openHandler = () => openModal(this, this.openButton);
      this.openButton.addEventListener('click', this.openHandler);
      instances.add(this);
      if (this.dataset.context === 'cart') {
        cartRefresh();
        return;
      }
      this.root = this.closest('.product-details');
      if (!this.root) return;
      this.changed = event => {
        const field = event.target;
        if (this.root.contains(field)) this.schedule();
      };
      // Capture also sees native handlers which stop propagation, without altering them.
      this.root.addEventListener('change', this.changed, true);
      this.root.addEventListener('input', this.changed, true);
      this.observer = new MutationObserver(records => {
        if (records.some(r => !this.contains(r.target))) this.schedule();
      });
      this.observer.observe(this.root, {childList: true, subtree: true, attributes: true,
        attributeFilter: ['data-price', 'data-last', 'disabled', 'value', 'data-has-combo']});
      this.schedule();
    }
    disconnectedCallback() {
      if (activeOwner === this) closeModal();
      this.openButton?.removeEventListener('click', this.openHandler);
      instances.delete(this);
      this.observer?.disconnect();
      this.root?.removeEventListener('change', this.changed, true);
      this.root?.removeEventListener('input', this.changed, true);
      cancelAnimationFrame(this.frame);
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      // Ella updates currentVariant and quantities synchronously in its own handler.
      this.frame = requestAnimationFrame(() => this.updateProduct());
    }
    updateProduct() {
      const c = config();
      const root = this.root;
      if (!validConfig(c) || !root || root.dataset.hasCombo === 'true') return this.hide();
      const form = root.querySelector(`[id="product-form-${this.dataset.productId}"]`);
      if (form?.querySelector('[name="selling_plan"]')?.value) return this.hide();
      const picker = root.querySelector('variant-radios, variant-selects');
      const selected = form?.querySelector('[name="id"]')?.value || this.dataset.initialVariant;
      let variant;
      if (picker && 'currentVariant' in picker) {
        variant = picker.currentVariant;
        if (!variant || String(variant.id) !== selected) return this.hide();
      } else if (picker) {
        try {
          variant = JSON.parse(picker.querySelector('[type="application/json"]').textContent).find(v => String(v.id) === selected);
        } catch (_) { return this.hide(); }
      } else if (selected === this.dataset.initialVariant) {
        variant = {price: this.dataset.initialPrice, available: this.dataset.initialAvailable === 'true'};
      }
      if (!variant?.available) return this.hide();
      const quantity = root.querySelector('.productView-product input[name="quantity"][data-price]');
      const count = quantity ? integer(quantity.value) : 1;
      if (quantity?.disabled || count === null || count < 1) return this.hide();
      // data-price is maintained by Ella for the selected variant, in cents.
      const price = integer(quantity ? quantity.dataset.price : variant.price);
      if (price === null) return this.hide();
      this.units = count;
      this.render(benefits(c, productTotal(price, count), 'product'));
    }
    hide() { this.model = null; this.hidden = true; this.removeAttribute('data-gerat-pending'); paintModal(this); }
    pending() {
      this.model = null;
      this.setAttribute('data-gerat-pending', '');
      if (activeOwner === this) modalStatus('Actualizando importe…');
    }
    render(model) {
      if (!model) return this.hide();
      this.model = model;
      const row = (selector, text, container) => {
        const node = this.querySelector(selector);
        if (node.textContent !== text) node.textContent = text;
        (container ? this.querySelector(container) : node).hidden = !text;
      };
      row('[data-gerat-finance]', model.finance, '[data-gerat-finance-row]');
      row('[data-gerat-shipping]', model.context === 'product' && !model.shippingEligible ? '' : model.shipping, '[data-gerat-shipping-row]');
      row('[data-gerat-more]', model.more);
      row('[data-gerat-summary]', !model.summary || model.summary === 'Ver opciones y condiciones'
        ? 'Ver opciones y condiciones de pago' : model.summary);
      this.querySelector('[data-gerat-credit-note]').hidden = !model.credit;
      this.removeAttribute('data-gerat-pending');
      this.hidden = false;
      paintModal(this);
    }
  }

  // One controller for both cart presentations. DOM and Ella pub/sub are primary.
  let version = 0, timer, controller, pendingAction = false, actionTimer;
  function invalidate() {
    version += 1;
    clearTimeout(timer);
    controller?.abort();
    cartInstances().forEach(el => el.pending());
  }
  function cartRefresh() {
    invalidate();
    if (!cartInstances().length || !validConfig(config()) || !config().cart) return;
    const revision = version;
    timer = setTimeout(async () => {
      if (pendingAction) return;
      const request = new AbortController();
      controller = request;
      const timeout = setTimeout(() => request.abort(), 8000);
      try {
        const root = new URL(config().root || '/', location.origin);
        if (root.origin !== location.origin) return;
        root.pathname = root.pathname.replace(/\/?$/, '/') + 'cart.js';
        const response = await fetch(root.href, {credentials: 'same-origin', cache: 'no-store', signal: request.signal});
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (revision !== version || pendingAction) return;
        const total = integer(cart.total_price), count = integer(cart.item_count);
        const model = cart.currency === 'MXN' && count > 0 && total !== null ? benefits(config(), total, 'cart') : null;
        cartInstances().forEach(el => { el.units = count; el.render(model); });
      } catch (_) {
        if (revision === version) cartInstances().forEach(el => el.hide());
      } finally { clearTimeout(timeout); }
    }, 100);
  }
  function completed() {
    pendingAction = false;
    clearTimeout(actionTimer);
    cartRefresh();
  }
  function starting() {
    pendingAction = true;
    invalidate();
    clearTimeout(actionTimer);
    // On missing completion/error events, stay hidden. Never re-display an old total.
    actionTimer = setTimeout(() => { pendingAction = false; }, 10000);
  }
  document.addEventListener('cart-update', completed);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, completed);
  }
  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-cart-quantity]')) starting();
  }, true);
  document.addEventListener('click', event => {
    const target = event.target.closest?.('[data-cart-remove], [data-update-coupon], #add-all-to-cart, #gift-wrapping, #cart-gift-wrapping, [name="add"], [data-cart-sidebar], cart-update-quantity .btn-quantity');
    if (!target) return;
    if (target.matches('[data-cart-sidebar]')) { if (!pendingAction) cartRefresh(); }
    else starting();
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.matches?.('form[action*="/cart/add"]')) starting();
  }, true);
  window.addEventListener('pageshow', () => { if (!pendingAction) cartRefresh(); });
  window.addEventListener('focus', () => { if (!pendingAction) cartRefresh(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && !pendingAction) cartRefresh(); });

  // Complement only: handles native coupons and additions that emit neither bus.
  // GET cart.js (our own read) and third-party/analytics requests are excluded.
  if (window.PerformanceObserver?.supportedEntryTypes?.includes('resource')) {
    const observer = new PerformanceObserver(list => {
      const relevant = list.getEntries().some(entry => {
        if (!['fetch', 'xmlhttprequest'].includes(entry.initiatorType)) return false;
        try {
          const url = new URL(entry.name);
          return url.origin === location.origin && /\/(?:cart\/(?:add|change|update|clear)(?:\.js)?\/?|discount\/[^/]+)\/?$/.test(url.pathname);
        } catch (_) { return false; }
      });
      if (relevant) completed();
    });
    observer.observe({type: 'resource'});
  }
  customElements.define('gerat-benefits', GeratBenefits);
})();
